import React, { useCallback } from "react";
import ReactDataTable from "../../../components/ReactDataTable";
import { useReportTimesheetContext } from "../../../context/ReportTimsheetContex";
import { leaveReportPayload, LeaveReportResponse } from "../../../model/timesheet";
import { formatAsSevenDigitNumber } from "../../../helper/convertionHealper";
import moment from "moment";
import CustomDatePicker from "../../../components/CustomDatePicker";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { FcSearch } from "react-icons/fc";

const LeaveReportPage: React.FC = () => {
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const { LeaveReportList, getLeaveReportList, setLeaveReportList } = useReportTimesheetContext();

  const excelSheetHeader: string[] = [
    "Employee ID",
    "Employee Name",
    "Project Name",
    "Leave Date",
    "Leave Hours",
    "Leave Type",
    "Leave Description",
  ];

  const columns = [
    {
      name: "Employee ID",
      selector: (row: LeaveReportResponse) => formatAsSevenDigitNumber(row.EmployeeId),
      sortable: true,
    },
    {
      name: "Employee Name",
      selector: (row: LeaveReportResponse) => row.EmployeeName,
      sortable: true,
    },
    {
      name: "Project Name",
      selector: (row: LeaveReportResponse) => row.ProjectName,
      sortable: true,
    },
    {
      name: "Leave Date",
      selector: (row: LeaveReportResponse) => row.LeaveDate,
      sortable: true,
    },
    {
      name: "Leave Hours",
      selector: (row: LeaveReportResponse) => row.LeaveHours,
      sortable: true,
    },
    {
      name: "Leave Type",
      selector: (row: LeaveReportResponse) => row.LeaveType,
      sortable: true,
    },
    {
      name: "Leave Description",
      selector: (row: LeaveReportResponse) => row.LeaveDescription,
      sortable: true,
    },
  ];

  const getLeavedetails = useCallback(async () => {
    if (startDate && endDate) {
      const payload: leaveReportPayload = {
        start_date: startDate ? moment(startDate).format("DD-MM-YYYY") : null,
        end_date: startDate ? moment(endDate).format("DD-MM-YYYY") : null,
      };
      await getLeaveReportList(payload);
    }
  }, [startDate, endDate]);

  const leaveReportListData = React.useMemo(() => {
    return LeaveReportList?.map((item) => ({
      EmployeeId: formatAsSevenDigitNumber(item.EmployeeId),
      EmployeeName: item.EmployeeName,
      ProjectName: item.ProjectName,
      LeaveDate: item.LeaveDate,
      LeaveHours: item.LeaveHours,
      LeaveType: item.LeaveType,
      LeaveDescription: item.LeaveDescription,
    }));
  }, [LeaveReportList]);

  const refreshTable = useCallback(() => {
    setEndDate(null);
    setStartDate(null);
    setLeaveReportList([]);
  }, []);

  return (
    <div>
      <div className=' gap-x-4 gap-y-4 grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 border-b pb-5 border-gray-200'>
        <div>
          <label
            htmlFor='Start Date'
            className='block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400'
          >
            Start date <span className='text-red-400'>*</span>
          </label>
          <div className='mt-2 flex items-center border border-gray-300 rounded-lg'>
            <CustomDatePicker
              onChange={setStartDate}
              selectedDate={startDate}
              isClearable={true}
              dateFormat='dd/MM/yyyy'
            />
          </div>
        </div>
        <div>
          <label
            htmlFor='End Date'
            className='block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400'
          >
            End Date <span className='text-red-400'>*</span>
          </label>
          <div className='mt-2'>
            <CustomDatePicker
              onChange={setEndDate}
              selectedDate={endDate}
              minDate={moment(startDate).isValid() ? moment(startDate).toDate() ?? null : new Date()}
              isClearable={true}
              dateFormat='dd/MM/yyyy'
            />
          </div>
        </div>

        <div className='flex space-x-4'>
          <button
            onClick={() => getLeavedetails()}
            className='flex  items-center px-4 py-2 mt-9 text-sm border border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-900 hover:bg-gray-200 text-gray-600 rounded-lg focus:outline-none '
          >
            <FcSearch className='h-5 w-5 text-white  mr-2' /> Search
          </button>
          <button
            onClick={() => refreshTable()}
            className='flex  items-center px-4 py-2 mt-9 text-sm bg-blue-700 hover:bg-blue-800 text-white rounded-lg focus:outline-none '
          >
            <ArrowPathIcon className='h-5 w-5 text-white mr-2' /> Reset
          </button>
        </div>
      </div>
      <ReactDataTable
        columns={columns}
        data={leaveReportListData ?? []}
        showFilter={leaveReportListData ? true : false}
        showExport={true}
        actionOption={false}
        ReportName={'LeaveReport'}
        searchLabelName='Search by Employee ID, Employee Name, etc'
        excelSheetHeader={excelSheetHeader}
      />
    </div>
  );
};

export default LeaveReportPage;
